/* eslint-disable no-console */
/* eslint-disable no-await-in-loop */
import { AGENTS_API_URL, HTTP_METHODS } from 'config';
import { request, postRequest } from '@quintoandar/wat-fetch';
import { addBreadcrumb } from '@quintoandar/fwp-error-monitoring';
import { hasAuthCookie } from '@quintoandar/wat-cookie';
import { getCookie } from '@quintoandar/fwp-cookie';
import { FEATURE_FLAGS } from 'enums/featureFlags';
import { getFeatureFlagEnabled } from 'utils/useFeatureFlag';
import { log } from 'utils/logUtils';

/**
 * Basic configuration for agents api calls
 *
 * @param {string} endpoint - The endpoint that will be called
 * @param {object} [filters] - The filters object for the current screen
 * @param {string} method - The HTTP method that will be used
 * @param {object} [payload] - The data
 * @param {string} [tab] - Current active tab
 *
 * @returns {Promise}
 */
export const agentsApiConfigWithoutRetry = async ({
  endpoint,
  method,
  payload,
  queryString,
  extraHeaders,
}) => {
  const authCookie = getAuthCookieValues();
  let config;
  let options;
  try {
    switch (method) {
      default:
      case HTTP_METHODS.GET:
      case HTTP_METHODS.PUT:
      case HTTP_METHODS.DELETE:
        options = {
          method,
          headers: extraHeaders,
          body: payload ? JSON.stringify(payload) : undefined,
        };
        if (queryString) {
          config = await request(
            `${AGENTS_API_URL}/${endpoint}?${queryString}`,
            options,
          );
        } else {
          config = await request(`${AGENTS_API_URL}/${endpoint}`, options);
        }
        break;
      case HTTP_METHODS.POST:
        config = await postRequest(
          `${AGENTS_API_URL}/${endpoint}`,
          payload,
          extraHeaders,
        );
        break;
    }
    addBreadcrumb({
      message: 'Request to api success',
      data: {
        endpoint,
        method,
        payload,
        queryString,
        extraHeaders,
        response: config,
        hasAuthCookie: authCookie.hasAuthCookie,
        authCookieStatus: authCookie.valueStatus,
        authCookieLength: authCookie.valueLength,
      },
    });
    return config;
  } catch (error) {
    addBreadcrumb({
      message: 'Request to api error',
      data: {
        endpoint,
        method,
        payload,
        queryString,
        extraHeaders,
        error,
        hasAuthCookie: authCookie.hasAuthCookie,
        authCookieStatus: authCookie.valueStatus,
        authCookieLength: authCookie.valueLength,
      },
    });
    throw error;
  }
};

export const agentsApiConfigWithRetry = async ({
  endpoint,
  method,
  payload,
  queryString,
  extraHeaders,
}) => {
  const authCookie = getAuthCookieValues();
  let lastError;
  let statusCode;
  const maxRetries =
    Number(await getFeatureFlagEnabled(FEATURE_FLAGS.RETRY_MAX_RETRIES)) || 3;
  const retryTimeoutMs =
    Number(await getFeatureFlagEnabled(FEATURE_FLAGS.RETRY_TIMEOUT_MS)) || 500;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const retryTimeoutValueMs = retryTimeoutMs * attempt;
    log('Calling api', {
      endpoint,
      method,
      attempt,
      maxRetries,
      retryTimeoutValueMs,
    });
    let config;
    let options;
    try {
      switch (method) {
        default:
        case HTTP_METHODS.GET:
        case HTTP_METHODS.PUT:
        case HTTP_METHODS.DELETE:
          options = {
            method,
            headers: extraHeaders,
            body: payload ? JSON.stringify(payload) : undefined,
          };
          if (queryString) {
            config = await request(
              `${AGENTS_API_URL}/${endpoint}?${queryString}`,
              options,
            );
          } else {
            config = await request(`${AGENTS_API_URL}/${endpoint}`, options);
          }
          break;
        case HTTP_METHODS.POST:
          config = await postRequest(
            `${AGENTS_API_URL}/${endpoint}`,
            payload,
            extraHeaders,
          );
          break;
      }
      addBreadcrumb({
        message: 'Request to api success',
        data: {
          endpoint,
          method,
          payload,
          queryString,
          extraHeaders,
          response: config,
        },
      });
      return config;
    } catch (error) {
      lastError = error;
      statusCode = lastError?.response?.status;
      const retryError =
        attempt <= maxRetries - 1 &&
        (isNetworkError(lastError) || isEmptyError(lastError));
      addBreadcrumb({
        message: 'Request to api error',
        data: {
          endpoint,
          method,
          payload,
          queryString,
          extraHeaders,
          error,
          hasAuthCookie: authCookie.hasAuthCookie,
          authCookieStatus: authCookie.valueStatus,
          authCookieLength: authCookie.valueLength,
          errorType: errorType(lastError),
          statusCode,
          retryError,
          attempt,
          maxRetries,
          retryTimeoutValueMs,
        },
      });
      if (retryError) {
        await new Promise((res) => setTimeout(res, retryTimeoutValueMs));
      } else {
        break;
      }
    }
  }
  log('all attempts failed, throwing error', {
    endpoint,
    errorType: errorType(lastError),
    statusCode,
    lastError,
  });
  throw lastError;
};

export const agentsApiConfig = async (params) => {
  const isRetryEnabled = await getFeatureFlagEnabled(
    FEATURE_FLAGS.AGENTS_API_RETRY,
  );
  if (isRetryEnabled) {
    return agentsApiConfigWithRetry(params);
  }
  return agentsApiConfigWithoutRetry(params);
};
const isNetworkError = (error) =>
  error?.name === 'TypeError' &&
  (error?.message === 'Failed to fetch' || error?.message === 'Load failed');

const isEmptyError = (error) => !error?.message && !error?.response;

const errorType = (error) => {
  if (isNetworkError(error)) {
    return 'network';
  }
  if (isEmptyError(error)) {
    return 'empty';
  }
  return 'other';
};

export default agentsApiConfig;

function getAuthCookieValues() {
  try {
    return {
      hasAuthCookie: hasAuthCookie(),
      valueStatus: getAuthCookieStatus(),
      valueLength: getCookie('5AJWT_AUTH')?.length,
    };
  } catch (error) {
    return {
      hasAuthCookie: false,
      valueStatus: 'error',
      valueLength: 0,
    };
  }
}
function getAuthCookieStatus() {
  const value = getCookie('5AJWT_AUTH');
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  return value.length > 0 ? 'present' : 'empty';
}
